import { useState } from "react";
import { Paperclip, FileText, Film, Plus } from "lucide-react";
import { useFileIndex } from "../../../lib/fileQueries";
import FileUploadDialog from "./FileUploadDialog";
import FilePreviewDrawer from "./FilePreviewDrawer";

/**
 * Master v5 Prompt 11 — a small, reusable "attach a file here" control.
 * Every direct contextual upload entry point (a practice Interaction
 * Instance, an Idea Card, a Section, a Session) is this SAME component
 * with a different `targetType`/`targetId` — the upload itself always
 * prefills that exact context and skips the classification picker (only
 * the General Files page asks "what is this related to?").
 */
export default function InlineFileAttachments({ workspaceId, targetType, targetId, label, compact = false }) {
  const { data, refetch } = useFileIndex(workspaceId, { targetType, targetId });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [previewFasId, setPreviewFasId] = useState(null);
  const items = data?.items || [];

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`inline-file-attachments-${targetType}-${targetId}`}>
      {items.map((entry) => (
        <button
          key={entry.asset.fas_id}
          onClick={() => setPreviewFasId(entry.asset.fas_id)}
          disabled={entry.asset.status !== "ready"}
          className="flex items-center gap-1 text-xs bg-ink/5 hover:bg-ink/10 px-2 py-1 rounded-full text-ink-soft disabled:opacity-50"
          data-testid={`inline-file-chip-${entry.asset.fas_id}`}
        >
          {entry.asset.kind === "video" ? <Film size={11} /> : <FileText size={11} />}
          <span className="truncate max-w-[9rem]">{entry.asset.original_filename}</span>
        </button>
      ))}
      <button
        onClick={() => setUploadOpen(true)}
        className="flex items-center gap-1 text-xs text-terracotta hover:text-terracotta/80 font-medium"
        data-testid={`inline-file-attach-btn-${targetType}-${targetId}`}
      >
        {compact ? <Plus size={12} /> : <Paperclip size={12} />} {items.length > 0 ? "Add file" : "Attach a file"}
      </button>

      <FileUploadDialog
        open={uploadOpen} onOpenChange={setUploadOpen} workspaceId={workspaceId}
        context={{ targetType, targetId, label }}
        onUploaded={() => refetch()}
      />
      <FilePreviewDrawer open={!!previewFasId} onOpenChange={(o) => !o && setPreviewFasId(null)} workspaceId={workspaceId} fasId={previewFasId} />
    </div>
  );
}
